import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";

const AccountPage = () => {
  const { t, lang } = useShop();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-GB", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="account-page">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="px-6 md:px-12 h-[72px] flex items-center justify-between border-b border-brown/10">
        <Link to="/" className="flex flex-col leading-none" data-testid="account-logo">
          <span className="font-serif text-2xl font-medium tracking-tight text-brown">VLP Dolap</span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-brown-muted mt-1">Volpar</span>
        </Link>
        <div className="flex items-center gap-5">
          {user?.role === "admin" && (
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 text-sm text-olive-dark hover:text-brown transition-colors duration-300"
              data-testid="account-admin-link"
            >
              <ShieldCheck size={15} /> {t.auth.adminPanel}
            </Link>
          )}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-brown-light hover:text-brown transition-colors duration-300"
            data-testid="account-back-home"
          >
            <ArrowLeft size={15} /> {t.auth.backHome}
          </Link>
        </div>
      </header>

      <main className="flex-1 px-6 md:px-12 py-16 max-w-[900px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-olive-dark mb-4">
            {t.auth.accountTitle}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brown" data-testid="account-welcome">
            {t.auth.welcome}, {user?.name}
          </h1>

          <div className="mt-12 bg-cream-dark border border-brown/10 p-8 md:p-10" data-testid="account-profile-card">
            <div className="flex items-center gap-5">
              <span className="w-16 h-16 rounded-full bg-olive/20 text-olive-dark font-serif text-3xl flex items-center justify-center">
                {user?.name?.charAt(0)?.toUpperCase()}
              </span>
              <div>
                <p className="font-serif text-2xl font-medium text-brown">{user?.name}</p>
                <p className="text-sm text-brown-light mt-1" data-testid="account-email">{user?.email}</p>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 border-t border-brown/10 pt-8">
              <div>
                <p className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">{t.auth.roleLabel}</p>
                <p className="mt-2 text-sm font-medium text-brown">
                  {user?.role === "admin" ? t.auth.roles.admin : t.auth.roles.customer}
                </p>
              </div>
              <div>
                <p className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">{t.auth.memberSince}</p>
                <p className="mt-2 text-sm font-medium text-brown">{memberSince}</p>
              </div>
            </div>
          </div>

          <p className="mt-8 text-sm font-light text-brown-light leading-relaxed" data-testid="account-orders-note">
            {t.auth.ordersNote}
          </p>

          <button
            onClick={handleLogout}
            className="mt-10 inline-flex items-center gap-3 px-8 py-4 rounded-full border border-brown/30 text-brown text-sm font-medium tracking-wide hover:border-olive hover:text-olive-dark transition-colors duration-300"
            data-testid="logout-button"
          >
            <LogOut size={16} /> {t.auth.logout}
          </button>
        </motion.div>
      </main>
    </div>
  );
};

export default AccountPage;
